/**
 * 递归创建不可修改的 Proxy，支持多层数据读取，拦截所有修改操作
 * @param target - 目标多层对象（支持对象、数组嵌套，排除 null/undefined）
 * @returns 不可修改但可正常读取的 Proxy 实例
 */
export function createImmutableProxy(target) {
    // 拦截器：完善类型定义，覆盖所有读写/修改操作
    const handler = {
        /**
         * 拦截属性读取（obj.key / arr[index] / 嵌套属性）
         * @param target - 当前层级的目标对象（可能是对象/数组）
         * @param prop - 要读取的属性名/索引（string | symbol | number）
         * @param receiver - 代理对象本身（维持原型链）
         * @returns 读取的属性值（基本类型直接返回，对象/数组返回 Proxy 实例）
         */
        get: (target, prop, receiver) => {
            // 检查属性是否存在（兼容 symbol/number 类型的属性/索引）
            if (Reflect.has(target, prop)) {
                const value = Reflect.get(target, prop, receiver);
                // 嵌套对象/数组需返回 Proxy 实例（已递归包装）
                return value;
            }
            return undefined;
        },
        /**
         * 拦截属性赋值（obj.key = val / arr[index] = val / arr.push() 等）
         * @param target - 当前层级的目标对象
         * @param prop - 要赋值的属性名/索引
         * @param value - 要赋的新值
         * @returns 永远不会返回（直接抛错）
         */
        set: (target, prop, value) => {
            const propType = Array.isArray(target) ? `index "${String(prop)}" (array)` : `property "${String(prop)}"`;
            throw new Error(`Modification of ${propType} is prohibited (the object has been frozen)`);
        },
        /**
         * 拦截属性删除（delete obj.key）
         * @param target - 当前层级的目标对象
         * @param prop - 要删除的属性名/索引
         * @returns 永远不会返回（直接抛错）
         */
        deleteProperty: (target, prop) => {
            throw new Error(`Deletion of property "${String(prop)}" is prohibited (the object has been frozen)`);
        },
        /**
         * 拦截新增属性（Object.defineProperty(obj, key, desc)）
         * @param target - 当前层级的目标对象
         * @param prop - 要新增的属性名
         * @param descriptor - 属性描述符
         * @returns 永远不会返回（直接抛错）
         */
        defineProperty: (target, prop, descriptor) => {
            throw new Error(`Addition of property "${String(prop)}" is prohibited (the object has been frozen)`);
        },
        /**
         * 拦截修改原型（obj.__proto__ = xxx / Object.setPrototypeOf）
         * @param target - 当前层级的目标对象
         * @param proto - 新的原型对象
         * @returns 永远不会返回（直接抛错）
         */
        setPrototypeOf: (target, proto) => {
            throw new Error('Modification of the object prototype is prohibited (the object has been frozen)');
        },
    };
    /**
     * 递归包装嵌套对象/数组，确保所有层级都被 Proxy 拦截
     * @param target - 当前要包装的值（对象/数组/基本类型）
     * @returns 包装后的值（对象/数组返回 Proxy，基本类型直接返回）
     */
    const wrapNested = (target) => {
        // 仅处理非 null 的对象/数组（排除基本类型）
        if (typeof target === 'object' && target !== null) {
            const newObj = {};
            // 递归包装所有自有属性
            Object.keys(target).forEach((key) => {
                // 类型断言：target 是对象，key 是其自有属性
                newObj[key] = wrapNested(target[key]);
            });
            // 包装为 Proxy 并返回（类型断言为 V，保持原类型结构）
            return new Proxy(newObj, handler);
        }
        else if (Array.isArray(target)) {
            return target.map((item) => wrapNested(item));
        }
        // 基本类型直接返回（string/number/boolean/symbol/null/undefined）
        return target;
    };
    // 启动递归包装，返回顶层 Proxy（类型断言为 Readonly<T>，提示开发者不可修改）
    return wrapNested(target);
}
